import { useMemo, useState } from "react";
import { Accordion, Button, Form, Icon, Input, Message, Modal, Segment } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "./DataTable";
import CombinedIcon from "./CombinedIcon";
import GuestHouseDropdown from "./GuestHouseDropdown";
import { ICON_OPTIONS } from "../../utils/iconOptions";

/* Matches RoomType.short_name's max_length on the backend. */
const SHORT_NAME_MAX = 20;

const EMPTY_ROOM_TYPE = {
  name: "",
  shortName: "",
  capacity: 1,
  description: "",
  basePrice: "",
  gstApplicable: true,
  autoBookThreshold: "",
  icon: "bed",
  extraIcon: "",
};
const NO_EXTRA_ICON_OPTIONS = [{ key: "none", value: "", text: "None" }, ...ICON_OPTIONS];

const columnHelper = createColumnHelper();

const fieldError = (message) => (message ? { content: message, pointing: "below" } : false);

/* Normalizes numeric-ish fields before comparing, so a value round-tripped
   through a number input (which yields strings) doesn't register as
   "changed" just because of a string/number type mismatch. */
const normalizeRoomType = (f) => ({
  name: f.name || "",
  shortName: (f.shortName || "").trim(),
  capacity: Number(f.capacity) || 0,
  description: f.description || "",
  basePrice: f.basePrice === "" || f.basePrice == null ? null : Number(f.basePrice),
  gstApplicable: f.gstApplicable !== false,
  autoBookThreshold: f.autoBookThreshold === "" || f.autoBookThreshold == null ? null : Number(f.autoBookThreshold),
  icon: f.icon || "bed",
  extraIcon: f.extraIcon || "",
});

export default function RoomTypesTab({ guestHouses, roomTypes, onAdd, onUpdate, onDelete }) {
  /* formModal: null | { mode: "add", ghId } | { mode: "edit", ghId, roomTypeId } */
  const [formModal, setFormModal] = useState(null);
  const [form, setForm] = useState(EMPTY_ROOM_TYPE);
  const [originalForm, setOriginalForm] = useState(EMPTY_ROOM_TYPE);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [ghFilter, setGhFilter] = useState("All");

  const rows = useMemo(() => {
    return guestHouses.flatMap((gh) =>
      (roomTypes?.[gh.id] || []).map((t) => ({
        ...t,
        guestHouseId: gh.id,
        guestHouseName: gh.name,
      })),
    );
  }, [roomTypes, guestHouses]);

  const filteredRows = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    return rows
      .filter((r) => ghFilter === "All" || Number(r.guestHouseId) === Number(ghFilter))
      .filter(
        (r) =>
          !q ||
          (r.guestHouseName || "").toLowerCase().includes(q) ||
          (r.name || "").toLowerCase().includes(q) ||
          (r.short_name || "").toLowerCase().includes(q) ||
          (r.description || "").toLowerCase().includes(q),
      );
  }, [rows, searchInput, ghFilter]);

  const openAdd = () => {
    setForm(EMPTY_ROOM_TYPE);
    setErrors({});
    setAdvancedOpen(false);
    setFormModal({ mode: "add", ghId: guestHouses[0]?.id || null });
  };

  const openEdit = (row) => {
    const initial = {
      name: row.name || "",
      shortName: row.short_name || "",
      capacity: row.capacity || 1,
      description: row.description || "",
      basePrice: row.basePrice ?? row.base_price ?? "",
      gstApplicable: row.gst_applicable !== false,
      autoBookThreshold: row.autoBookThreshold ?? row.auto_book_threshold_percent ?? "",
      icon: row.attributes?.icon || "bed",
      extraIcon: row.attributes?.extraIcon || "",
    };
    setForm(initial);
    setOriginalForm(initial);
    setErrors({});
    setAdvancedOpen(false);
    setFormModal({ mode: "edit", ghId: row.guestHouseId, roomTypeId: row.id });
  };

  const dirty =
    formModal?.mode !== "edit" ||
    JSON.stringify(normalizeRoomType(form)) !== JSON.stringify(normalizeRoomType(originalForm));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.capacity || form.capacity < 1) e.capacity = "Enter a valid capacity.";
    if (formModal?.mode === "add" && !formModal.ghId) e.guestHouse = "Select a guest house.";
    return e;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      if (formModal.mode === "edit") {
        await onUpdate(formModal.ghId, formModal.roomTypeId, form);
      } else {
        await onAdd(formModal.ghId, form);
      }
      setFormModal(null);
    } catch (e) {
      setErrors({
        save: e.message || `Failed to ${formModal.mode === "edit" ? "update" : "add"} room type.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await onDelete(deleteConfirm.guestHouseId, deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (e) {
      setDeleteError(e.message || "Failed to delete room type.");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("guestHouseName", { header: "Guest House" }),
      columnHelper.accessor("name", { header: "Name" }),
      columnHelper.accessor("short_name", {
        header: "Short Name",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("capacity", { header: "Capacity" }),
      columnHelper.accessor((row) => row.basePrice ?? row.base_price, {
        id: "basePrice",
        header: "Base Price",
        cell: (info) => (info.getValue() ? `₹${info.getValue()}` : "—"),
      }),
      columnHelper.accessor((row) => row.gst_applicable !== false, {
        id: "gstApplicable",
        header: "GST on rent",
        cell: (info) => (info.getValue() ? "Charged" : "Not charged"),
      }),
      columnHelper.accessor((row) => row.autoBookThreshold ?? row.auto_book_threshold_percent, {
        id: "autoBookThreshold",
        header: "Auto-Book %",
        cell: (info) =>
          info.getValue() !== null && info.getValue() !== undefined && info.getValue() !== ""
            ? `${info.getValue()}%`
            : "—",
      }),
      columnHelper.accessor("description", {
        header: "Description",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: (info) => (
          <Button.Group size="small">
            <Button basic onClick={() => openEdit(info.row.original)}>
              <Icon name="edit" />
              Edit
            </Button>
            <Button
              basic
              negative
              onClick={() => {
                setDeleteError("");
                setDeleteConfirm(info.row.original);
              }}
            >
              <Icon name="trash" />
              Delete
            </Button>
          </Button.Group>
        ),
      }),
    ],
    [],
  );

  return (
    <div>
      <Segment basic vertical>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Search by name, guest house, description…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              action={
                searchInput
                  ? { icon: "times circle", basic: true, onClick: () => setSearchInput("") }
                  : undefined
              }
              style={{ minWidth: 340 }}
            />
            {guestHouses.length > 1 && (
              <GuestHouseDropdown
                value={ghFilter}
                options={[
                  { value: "All", label: "All Guest Houses" },
                  ...guestHouses.map((g) => ({ value: g.id, label: g.name })),
                ]}
                onChange={(v) => setGhFilter(v === "All" ? "All" : Number(v))}
              />
            )}
          </div>
          <Button primary onClick={openAdd}>
            <Icon name="plus" />
            Add Room Type
          </Button>
        </div>
      </Segment>

      <DataTable
        data={filteredRows}
        columns={columns}
        emptyMessage={
          rows.length === 0
            ? "No room types found. Add one to get started."
            : "No room types match your filters."
        }
      />

      {/* Add / Edit Room Type Modal */}
      <Modal
        open={!!formModal}
        onClose={() => setFormModal(null)}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <Icon name={formModal?.mode === "edit" ? "edit" : "plus circle"} />
          {formModal?.mode === "edit" ? "Edit Room Type" : "Add Room Type"}
        </Modal.Header>
        <Modal.Content>
          <Form error={!!errors.save}>
            <Form.Group widths="equal">
              {formModal?.mode === "edit" ? (
                <Form.Input
                  label="Guest House"
                  disabled
                  value={
                    guestHouses.find((g) => g.id === formModal.ghId)?.name || ""
                  }
                />
              ) : (
                <Form.Select
                  label="Guest House"
                  required
                  placeholder="Select guest house…"
                  options={guestHouses.map((g) => ({ key: g.id, text: g.name, value: g.id }))}
                  value={formModal?.ghId || ""}
                  error={fieldError(errors.guestHouse)}
                  onChange={(_, data) =>
                    setFormModal((m) => ({ ...m, ghId: data.value }))
                  }
                />
              )}
              <Form.Input
                label="Name"
                required
                placeholder="e.g. Single AC"
                value={form.name}
                error={fieldError(errors.name)}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              {/* Shown in the dashboard's "Rooms Requested" column in place of
                  the full name; blank keeps the full name there. */}
              <Form.Input
                label="Short Name"
                placeholder="e.g. SAC"
                maxLength={SHORT_NAME_MAX}
                value={form.shortName}
                onChange={(e) => setForm((f) => ({ ...f, shortName: e.target.value }))}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Capacity"
                required
                type="number"
                min={1}
                max={10}
                value={form.capacity}
                error={fieldError(errors.capacity)}
                onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
              />
              <Form.Input
                label="Description"
                placeholder="e.g. Single occupancy with air conditioning"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
              <Form.Input
                label="Base Price (₹/night)"
                type="number"
                min={0}
                placeholder="e.g. 1500"
                value={form.basePrice}
                onChange={(e) => setForm((f) => ({ ...f, basePrice: e.target.value }))}
              />
            </Form.Group>
            <Form.Checkbox
              id="room-type-gst"
              label="Charge GST on this room's rent (never charged on official bookings)"
              checked={form.gstApplicable}
              onChange={(_, d) => setForm((f) => ({ ...f, gstApplicable: d.checked }))}
            />

            <Accordion>
              <Accordion.Title
                active={advancedOpen}
                onClick={() => setAdvancedOpen((v) => !v)}
                index={0}
              >
                <Icon name="dropdown" />
                Additional Settings
              </Accordion.Title>
              <Accordion.Content active={advancedOpen}>
                <Form.Group widths="equal">
                  <Form.Select
                    label="Base Icon"
                    search
                    options={ICON_OPTIONS}
                    value={form.icon}
                    onChange={(_, data) => setForm((f) => ({ ...f, icon: data.value }))}
                  />
                  <Form.Select
                    label="Extra Icon"
                    search
                    options={NO_EXTRA_ICON_OPTIONS}
                    value={form.extraIcon}
                    onChange={(_, data) => setForm((f) => ({ ...f, extraIcon: data.value }))}
                  />
                  <Form.Input
                    label="Auto-Book Threshold (%)"
                    type="number"
                    min={0}
                    max={100}
                    placeholder="e.g. 50"
                    value={form.autoBookThreshold}
                    onChange={(e) => setForm((f) => ({ ...f, autoBookThreshold: e.target.value }))}
                  />
                </Form.Group>
                <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)", marginTop: -8 }}>
                  Auto-confirm bookings for this room type when at least this % of its rooms are
                  free. Leave blank to opt this room type out of auto-booking.
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>Preview:</span>
                  <CombinedIcon
                    baseIcon={form.icon || "bed"}
                    color="blue"
                    badges={form.extraIcon ? [form.extraIcon] : []}
                  />
                </div>
              </Accordion.Content>
            </Accordion>

            {errors.save && <Message negative content={errors.save} />}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setFormModal(null)}>
            Cancel
          </Button>
          <Button primary loading={saving} disabled={saving || !dirty} onClick={handleSave}>
            <Icon name="save" />
            {saving
              ? "Saving…"
              : formModal?.mode === "edit"
                ? "Save Changes"
                : "Add Room Type"}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        size="mini"
      >
        <Modal.Header>
          <Icon name="trash" /> Delete Room Type
        </Modal.Header>
        <Modal.Content>
          <p>
            Delete <strong>{deleteConfirm?.name}</strong>?
          </p>
          <p className="bhu-confirm-sub">This cannot be undone.</p>
          {deleteError && <Message negative content={deleteError} />}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button negative loading={deleting} disabled={deleting} onClick={handleDelete}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
